import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useLanguage } from '@/contexts/LanguageContext';

const AboutPage = () => {
  const { t, lang } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 bg-stone-800 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=library%20interior%20with%20tall%20bookshelves%20filled%20with%20law%20and%20political%20science%20books%2C%20warm%20atmospheric%20lighting%2C%20editorial%20photography%2C%20cinematic%20mood%2C%20deep%20navy%20and%20gold%20tones%2C%20dramatic%20shadows%2C%20academic%20gravitas%2C%20vintage%20aesthetic%2C%20grand%20architecture&width=1600&height=700&seq=about-hero&orientation=landscape"
            alt="Library"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-800/80 to-stone-800" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">{t('About', '소개')}</p>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
            {t('Who I Am & Why This Exists', '나와 이 웹사이트에 관하여')}
          </h1>

        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="aspect-square rounded-2xl overflow-hidden bg-stone-100 mb-5">
                  <img
                    src="https://static.readdy.ai/image/87a8138ca64b05ac1815bceaa72b9a44/f0d3be8236f87e4a826c5bb3c4e48132.jpeg"
                    alt="Profile"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-stone-600">
                    <div className="w-7 h-7 flex items-center justify-center bg-stone-50 rounded-lg text-base leading-none">
                      🇰🇷
                    </div>
                    <span>{t('Republic of Korea', '대한민국')}</span>
                  </div>
                  {[
                    { icon: 'ri-plane-line', label: t('International Student', '유학생') },
                    { icon: 'ri-translate-2', label: t('Korean · English', '한국어 · 영어로') },
                    { icon: 'ri-scales-3-line', label: t('Politics, Rights & International Order', '정치, 권리 및 국제 질서') },
                  ].map(({ icon, label }) => (
                    <div key={label} className="flex items-center gap-3 text-sm text-stone-600">
                      <div className="w-7 h-7 flex items-center justify-center bg-stone-50 rounded-lg">
                        <i className={`${icon} text-stone-400`} />
                      </div>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-stone-100">
                  <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">{t('Categories', '카테고리')}</p>
                  <div className="flex flex-wrap gap-2">
                    {(lang === 'ko'
                      ? ['국제 문제·국제 정세', '국제 질서', '인권·권리와 자유', '정치', '정의와 자유', '법과 사회']
                      : ['Global Affairs', 'International Order', 'Rights and Freedoms', 'Politics', 'Justice and Freedom', 'Law and Society']
                    ).map((tag) => (
                      <span key={tag} className="text-xs px-2.5 py-1 bg-stone-50 border border-stone-100 rounded-full text-stone-600">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-2 space-y-10">

              {/* Who I Am */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-stone-800 rounded-xl">
                    <i className="ri-user-heart-line text-white text-sm" />
                  </div>
                  <h2 className="text-xl font-bold text-stone-800">{t('Who I Am', '나는 누구인가')}</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {t(
                      "I am a Korean high school student currently studying in the United States, and someone deeply interested in international affairs and the world beyond the surface of what is familiar.",
                      "저는 미국의 고등학교에 재학 중인 한국인 학생이며 국제 사회와 세계에 관심을 가지고 있는 사람입니다."
                    )}
                  </p>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {t(
                      'The world we live in often feels so ordinary, so familiar, that it is easily accepted as simply "normal." But the moment we look a little more closely, we begin to see how much silence, tension, and how many overlooked problems exist beneath that surface. The same reality can take on entirely different meanings depending on whose perspective it is seen from, and what kind of life and values shape that perspective. Even the explanations we take for granted begin to appear unfamiliar the moment the angle of the question shifts. I am someone who lingers at exactly that point. I have never believed that accumulating more facts alone is enough. I have wanted to understand the contexts in which those facts are placed, the reasons they are justified, and what they reveal as well as what they conceal. Why are some things treated as though they are the most natural order of the world, while other problems remain unnamed until the very end? Why can the same reality be read as stability by some, and as exclusion or insecurity by others? I am someone who cannot simply pass over those differences, but feels compelled to sit with them and think through them deeply.',
                      "우리가 살아가는 이 세계는 매우 익숙하며 흔한 '보통'으로 여겨지지만 조금만 더 자세히 들여다보면 그 안에는 수많은 침묵과 긴장, 그리고 너무 쉽게 지나쳐버린 문제들이 숨어 있습니다. 같은 것도 누구의 시선에서 바라보느냐에 따라, 어떤 삶과 가치관으로 여기냐에 따라 전혀 다른 의미를 갖게 되고, 익숙한 설명 역시 질문의 각도를 바꾸는 순간 낯선 모습으로 다시 드러납니다. 저는 바로 이러한 것들에 대해 생각하는 사람입니다. 더 많은 사실을 쌓는 것만으로는 충분하지 않으며, 그 사실들이 어떤 맥락 속에서 놓이고 어떤 이유로 정당화되며 무엇을 보이게 하고 무엇을 감추는지를 알아가고 싶었습니다. 왜 어떤 것은 너무도 당연한 순리처럼 여겨지고, 어떤 문제는 끝내 이름 붙여지지 못한 채 남는지. 왜 같은 현실이 어떤 사람에게는 안정으로, 다른 사람에게는 불안이나 배제로 읽히는지. 저는 그러한 차이를 그냥 지나치지 않고 깊게 고뇌해 보는 사람입니다."
                    )}
                  </p>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {t(
                      "What best reveals who I am is my life and my values and I will share the way I see the world I have drawn from it.",
                      "제가 어떤 사람인지를 가장 잘 보여 주는 것은 저의 삶과 제 가치관 그 자체이며 그것을 여러분께 다양한 곳에서 다양한 모습으로 보여드리겠습니다."
                    )}
                  </p>
                </div>
              </div>

              {/* Why This Website */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-stone-800 rounded-xl">
                    <i className="ri-lightbulb-line text-white text-sm" />
                  </div>
                  <h2 className="text-xl font-bold text-stone-800">{t('Why This Website', '왜 이 웹사이트인가')}</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {t(
                      "I created LUVing because I needed a space that was entirely my own. Not shaped by social media algorithms, and not confined by school assignments or institutional expectations, but a place where the things I genuinely care about and think about deeply could gather, unfold, and remain.",
                      "저는 완전히 제 것인 공간이 필요해서 LUVing을 만들게 되었습니다. SNS 알고리즘도, 학교나 다른 교육 기관의 수업 과제도 아닌, 제가 실제로 관심을 갖고 깊이 생각하는 것들이 차곡차곡 만개하다 끝내 흩날릴 곳."
                    )}
                  </p>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {t(
                      "There are many forms of love, and there are also places in this world that desperately need it. Some people call that form of love attention. I believe there are still many things in this world that need to be seen more carefully, more thoughtfully, and more fully.",
                      "세상에는 다양한 사랑이 있고 사랑이 필요한 곳 또한 있습니다. 누군가는 이러한 형태의 사랑을 관심이라 이름 붙여 칭하기도 합니다. 세상에는 관심이 필요한 것들이 있습니다."
                    )}
                  </p>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {t(
                      "This website exists to explain complex international issues, to write across two languages, and to share these ideas with people who may not otherwise have encountered them. I created it in the hope of scattering even a small measure of understanding and curiosity about the international world.",
                      "복잡한 국제적 이야기를 설명하고, 두 언어로 작성하고, 이러한 주제를 접하지 못했을 수도 있는 사람들과 공유하는 것. 사람들에게 이 국제적 세계에 작은 이해와 관심을 흩뿌리기 위해 이 웹사이트를 만들었습니다."
                    )}
                  </p>
                  <p className="text-stone-800 text-sm font-medium">
                    ch.on1yy1@gmail.com
                  </p>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {t(
                      "Please direct all business inquiries and collaboration requests to the email above.",
                      "모든 문의, 연락, 협업은 위의 이메일로 보내주세요."
                    )}
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
